import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Search, Users } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { supabase } from "../lib/supabaseClient";
import { usePlatformSecurityCapabilities } from "../context/PlatformSecurityCapabilityContext.jsx";

const FUNCTION_NAME = "sales_affiliate_admin_visibility";
const REQUIRED_CAPABILITY = "MANAGE_AFFILIATE_POLICY_OVERRIDES";

function displayState(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  return raw
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function csvEscape(value) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(rows) {
  const columns = [
    ["full_name", "Name"],
    ["email", "Email"],
    ["lifecycle_state", "Lifecycle State"],
    ["created_at", "Signup Date"],
    ["application_state", "Application State"],
    ["application_submitted_at", "Application Submitted"],
    ["primary_industry", "Primary Industry"],
    ["profile_confirmed", "Profile Confirmed"],
    ["agreement_status", "Agreement Status"],
    ["agreement_version", "Agreement Version"],
    ["training_status", "Required Training"],
    ["activated_at", "Activated Date"],
    ["payment_setup_state", "Payment Setup"],
    ["active_prospect_count", "Active Protected Prospects"],
    ["converted_prospect_count", "Converted Prospects"],
  ];

  const lines = [columns.map(([, label]) => csvEscape(label)).join(",")];

  rows.forEach((row) => {
    lines.push(
      columns
        .map(([key]) => {
          const value =
            key === "profile_confirmed"
              ? row[key] === true
                ? "Yes"
                : "No"
              : row[key];
          return csvEscape(value);
        })
        .join(","),
    );
  });

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `sales_affiliates_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-3xl font-black text-slate-950">{value}</div>
    </div>
  );
}

export default function SalesAffiliateAdmin() {
  const { status: capabilityStatus, hasCapability } =
    usePlatformSecurityCapabilities();

  const authorized =
    capabilityStatus === "ready" && hasCapability(REQUIRED_CAPABILITY);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    pre_activation: 0,
    active: 0,
    manual_review: 0,
    payment_verified: 0,
    payment_incomplete: 0,
  });
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("ALL");
  const [industryFilter, setIndustryFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");

  async function load() {
    if (!authorized) return;
    setLoading(true);
    setError("");

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        FUNCTION_NAME,
        { body: {} },
      );

      if (invokeError) throw invokeError;
      if (!data?.ok || !Array.isArray(data?.items)) {
        throw new Error(data?.error || "Unable to load Sales Affiliate visibility.");
      }

      setRows(data.items);
      setSummary(
        data.summary || {
          total: data.items.length,
          pre_activation: 0,
          active: 0,
          manual_review: 0,
          payment_verified: 0,
          payment_incomplete: 0,
        },
      );
      setGeneratedAt(data.generated_at || null);
    } catch (err) {
      setRows([]);
      setError(
        String(
          err?.message ||
            err ||
            "Unable to load Sales Affiliate visibility.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authorized) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  const industries = useMemo(() => {
    const values = new Set();
    rows.forEach((row) => {
      const value = String(row.primary_industry || "").trim();
      if (value) values.add(value);
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        lifecycleFilter !== "ALL" &&
        row.lifecycle_state !== lifecycleFilter
      ) {
        return false;
      }

      if (
        industryFilter !== "ALL" &&
        String(row.primary_industry || "") !== industryFilter
      ) {
        return false;
      }

      if (
        paymentFilter === "VERIFIED" &&
        row.payment_setup_state !== "VERIFIED"
      ) {
        return false;
      }

      if (
        paymentFilter === "INCOMPLETE" &&
        row.payment_setup_state === "VERIFIED"
      ) {
        return false;
      }

      if (!normalizedQuery) return true;

      const haystack = [
        row.full_name,
        row.email,
        row.lifecycle_state,
        row.primary_industry,
        row.application_state,
        row.payment_setup_state,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      return haystack.includes(normalizedQuery);
    });
  }, [rows, query, lifecycleFilter, industryFilter, paymentFilter]);

  if (capabilityStatus === "idle" || capabilityStatus === "loading") {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm font-semibold">
            Checking Sales Affiliate administration authority…
          </div>
        </main>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-800">
            This page requires the {REQUIRED_CAPABILITY} capability.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-[1500px] px-6 py-8 sm:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.28em] text-blue-600">
                <Users size={16} />
                Super Admin
              </div>
              <h1 className="mt-2 text-4xl font-black tracking-tight text-slate-950">
                Sales Affiliates
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Read-only operational visibility across the Sales Affiliate
                lifecycle. This page does not change Affiliate records,
                activation, commissions, payouts, calculations, or reports.
              </p>
              {generatedAt ? (
                <div className="mt-2 text-xs text-slate-400">
                  Refreshed {formatDate(generatedAt)}
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => downloadCsv(filteredRows)}
                disabled={loading || filteredRows.length === 0}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>

          <section className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <SummaryCard label="Total Affiliates" value={summary.total} />
            <SummaryCard label="Pre-Activation" value={summary.pre_activation} />
            <SummaryCard label="Active" value={summary.active} />
            <SummaryCard label="Manual Review" value={summary.manual_review} />
            <SummaryCard label="Payment Verified" value={summary.payment_verified} />
            <SummaryCard
              label="Payment Incomplete"
              value={summary.payment_incomplete}
            />
          </section>

          <section className="mt-7 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-3 xl:grid-cols-[minmax(280px,1fr)_220px_220px_220px]">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search name, email, industry, lifecycle, or payment status"
                  className="w-full rounded-xl border border-slate-200 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <select
                value={lifecycleFilter}
                onChange={(event) => setLifecycleFilter(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold"
              >
                <option value="ALL">All lifecycle states</option>
                <option value="REGISTERED_UNVERIFIED">Registered Unverified</option>
                <option value="PRE_ACTIVATION">Pre-Activation</option>
                <option value="MANUAL_REVIEW_REQUIRED">Manual Review</option>
                <option value="DISAPPROVED">Disapproved</option>
                <option value="ACTIVE">Active</option>
              </select>

              <select
                value={industryFilter}
                onChange={(event) => setIndustryFilter(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold"
              >
                <option value="ALL">All industries</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>

              <select
                value={paymentFilter}
                onChange={(event) => setPaymentFilter(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold"
              >
                <option value="ALL">All Payment Setup</option>
                <option value="VERIFIED">Verified</option>
                <option value="INCOMPLETE">Incomplete / not verified</option>
              </select>
            </div>

            {error ? (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
                {error}
              </div>
            ) : null}

            <div className="mt-5 text-sm font-semibold text-slate-500">
              Showing {filteredRows.length} of {rows.length} Sales Affiliates
            </div>

            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-[1500px] w-full text-left">
                <thead className="bg-slate-50 text-[11px] font-black uppercase tracking-[0.16em] text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Affiliate</th>
                    <th className="px-4 py-3">Lifecycle</th>
                    <th className="px-4 py-3">Primary Industry</th>
                    <th className="px-4 py-3">Application</th>
                    <th className="px-4 py-3">Profile</th>
                    <th className="px-4 py-3">Agreement</th>
                    <th className="px-4 py-3">Training</th>
                    <th className="px-4 py-3">Payment Setup</th>
                    <th className="px-4 py-3">Active Protected</th>
                    <th className="px-4 py-3">Converted</th>
                    <th className="px-4 py-3">Activated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-sm text-slate-500">
                        Loading Sales Affiliate visibility…
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-sm text-slate-500">
                        No Sales Affiliates match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr key={row.affiliate_id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-bold text-slate-950">
                            {row.full_name || "Unnamed Affiliate"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {row.email || "—"}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Joined {formatDate(row.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold">
                          {displayState(row.lifecycle_state)}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {row.primary_industry || "—"}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="font-semibold">
                            {displayState(row.application_state)}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            {formatDate(row.application_submitted_at)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold">
                          {row.profile_confirmed ? "Confirmed" : "Not confirmed"}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="font-semibold">
                            {displayState(row.agreement_status)}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            {row.agreement_version || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="font-semibold">
                            {displayState(row.training_status)}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            {row.completed_required_training_count}/
                            {row.required_training_count} required
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold">
                          {displayState(row.payment_setup_state)}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold">
                          {row.active_prospect_count ?? 0}
                        </td>
                        <td className="px-4 py-4 text-sm font-bold">
                          {row.converted_prospect_count ?? 0}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {formatDate(row.activated_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
